<template>
    <div
        v-if="archiveStats || archiveStatsLoading"
        class="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 mb-6"
    >
        <div class="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-4 mb-4">
            <h2 class="text-slate-800 dark:text-slate-100 text-base font-semibold">All Time Stats</h2>
            <select
                v-if="archiveStats && archiveStats.years.length > 0"
                v-model="selectedYear"
                class="archive-stats-select text-sm border border-slate-200 dark:border-slate-700 rounded-md pl-3 pr-8 py-1.5 text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-700 focus:outline-none focus:ring-1 focus:ring-orange-500"
                @change="onYearChange"
            >
                <option :value="null">All time</option>
                <option v-for="year in archiveStats.years" :key="year" :value="year">
                    {{ year }}
                </option>
            </select>
        </div>

        <div v-if="archiveStatsLoading" class="relative min-h-[40px] flex items-center justify-center">
            <LoadingInline :loading="true" size="small" />
        </div>

        <div v-else-if="archiveStats" class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">

            <!-- Total Trips -->
            <div class="flex items-center gap-3 relative min-h-[40px]">
                <div class="bg-slate-100 dark:bg-slate-700 p-2.5 rounded-full flex items-center justify-center w-10 h-10 shrink-0">
                    <Icon icon="trip" class="text-slate-600 dark:text-slate-300" :height="18" :width="18" />
                </div>
                <div>
                    <p class="text-slate-800 dark:text-slate-100 text-base font-semibold">
                        {{ formatNumber(archiveStats.total_trips) }}
                    </p>
                    <p class="text-slate-500 dark:text-slate-400 text-sm">Trips</p>
                </div>
            </div>

            <!-- Total Redemptions -->
            <div class="flex items-center gap-3 relative min-h-[40px]">
                <div class="bg-slate-100 dark:bg-slate-700 p-2.5 rounded-full flex items-center justify-center w-10 h-10 shrink-0">
                    <Icon icon="track" class="text-slate-600 dark:text-slate-300" :height="18" :width="18" />
                </div>
                <div>
                    <p class="text-slate-800 dark:text-slate-100 text-base font-semibold">
                        {{ formatNumber(archiveStats.total_redemptions) }}
                    </p>
                    <p class="text-slate-500 dark:text-slate-400 text-sm">Redemptions</p>
                </div>
            </div>

            <!-- Total Cash Value -->
            <div class="flex items-center gap-3 relative min-h-[40px]">
                <div class="bg-slate-100 dark:bg-slate-700 p-2.5 rounded-full flex items-center justify-center w-10 h-10 shrink-0">
                    <Icon icon="dollar-bill" class="text-slate-600 dark:text-slate-300" :height="18" :width="18" />
                </div>
                <div>
                    <p class="text-slate-800 dark:text-slate-100 text-base font-semibold">
                        {{ formatCurrency(archiveStats.total_cash_price) }}
                    </p>
                    <p class="text-slate-500 dark:text-slate-400 text-sm">Total cash value</p>
                </div>
            </div>

            <!-- Total $ Spent (with popover) -->
            <div class="flex items-center gap-3 relative min-h-[40px]">
                <div class="bg-slate-100 dark:bg-slate-700 p-2.5 rounded-full flex items-center justify-center w-10 h-10 shrink-0">
                    <Icon icon="dollar-coin" class="text-slate-600 dark:text-slate-300" :height="18" :width="18" />
                </div>
                <div>
                    <div class="flex items-center gap-1">
                        <p class="text-slate-800 dark:text-slate-100 text-base font-semibold">
                            {{ formatCurrency(archiveStats.total_cash_spent) }}
                        </p>
                        <button
                            v-if="archiveStats.total_cash_spent > 0"
                            @click.stop="togglePopover('cash')"
                            class="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                        >
                            <Icon icon="chevron-down" :width="14" :height="14" />
                        </button>
                    </div>
                    <p class="text-slate-500 dark:text-slate-400 text-sm">Total $ spent</p>

                    <div
                        v-if="openPopover === 'cash'"
                        v-click-outside="closePopovers"
                        class="absolute top-full left-0 z-20 mt-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg w-60 p-4"
                    >
                        <div class="flex justify-between mb-3">
                            <h3 class="text-slate-500 dark:text-slate-400 text-[14px] font-medium">Cash cost</h3>
                            <span class="text-[14px] text-slate-800 dark:text-slate-100">{{ formatCurrency(archiveStats.total_cash_amount) }}</span>
                        </div>
                        <div class="flex justify-between">
                            <h3 class="text-slate-500 dark:text-slate-400 text-[14px] font-medium">Taxes & fees</h3>
                            <span class="text-[14px] text-right text-slate-800 dark:text-slate-100">{{ formatCurrency(archiveStats.total_taxes_fees) }}</span>
                        </div>
                        <div class="mt-3 w-full h-0.5 bg-slate-200 dark:bg-slate-700"></div>
                        <div class="mt-3 flex justify-between">
                            <h3 class="text-slate-500 dark:text-slate-400 text-[14px] font-medium">Total</h3>
                            <span class="text-[14px] text-right text-slate-800 dark:text-slate-100">{{ formatCurrency(archiveStats.total_cash_spent) }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Total Points Spent (with popover) -->
            <div class="flex items-center gap-3 relative min-h-[40px]">
                <div class="bg-slate-100 dark:bg-slate-700 p-2.5 rounded-full flex items-center justify-center w-10 h-10 shrink-0">
                    <Icon icon="points" class="text-slate-600 dark:text-slate-300" :height="18" :width="18" />
                </div>
                <div>
                    <div class="flex items-center gap-1">
                        <p class="text-slate-800 dark:text-slate-100 text-base font-semibold">
                            {{ formatNumber(archiveStats.total_points_spent) }}
                        </p>
                        <button
                            v-if="archiveStats.total_points_spent > 0"
                            @click.stop="togglePopover('points')"
                            class="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                        >
                            <Icon icon="chevron-down" :width="14" :height="14" />
                        </button>
                    </div>
                    <p class="text-slate-500 dark:text-slate-400 text-sm">Total points spent</p>

                    <div
                        v-if="openPopover === 'points'"
                        v-click-outside="closePopovers"
                        class="absolute top-full left-0 z-20 mt-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg w-64 p-4"
                    >
                        <div
                            v-for="program in archiveStats.points_by_program"
                            :key="program.payment_program_id"
                            class="flex justify-between items-center mb-3"
                        >
                            <img
                                v-if="program.payment_program?.program_image"
                                :src="`${cdnUrl}/${program.payment_program.program_image.path}/_small/${program.payment_program.program_image.file_name}`"
                                :alt="program.payment_program?.name"
                                class="max-w-[120px] h-[20px] object-contain dark:bg-slate-200 dark:rounded dark:px-1"
                            />
                            <span v-else class="text-[12px] text-slate-700 dark:text-slate-200">{{ program.payment_program?.name }}</span>
                            <span class="text-[12px] text-slate-800 dark:text-slate-100">{{ formatNumber(program.payment_points) }}</span>
                        </div>
                        <div class="mt-2 w-full h-0.5 bg-slate-200 dark:bg-slate-700"></div>
                        <div class="mt-3 flex justify-between">
                            <h3 class="text-slate-500 dark:text-slate-400 text-[12px] font-medium">Total</h3>
                            <span class="text-[12px] text-right text-slate-800 dark:text-slate-100">{{ formatNumber(archiveStats.total_points_spent) }}</span>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>
<script setup>
import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import currency from 'currency.js';
import { useTripsStore } from '@/store/trips';
import Icon from '@/components/ui/icon/icon.vue';
import LoadingInline from '@/components/ui/loading-state/loading-state.vue';

const tripsStore = useTripsStore();
const { archiveStats, archiveStatsLoading } = storeToRefs(tripsStore);

const selectedYear = ref(null);
const openPopover = ref(null);

const cdnUrl = computed(() => window.cdnUrl);

const formatCurrency = (amount) => {
    return currency(amount ?? 0, { symbol: '$', precision: 2 }).format();
};

const formatNumber = (number) => {
    return Intl.NumberFormat('en-US').format(number ?? 0);
};

const togglePopover = (name) => {
    openPopover.value = openPopover.value === name ? null : name;
};

const closePopovers = () => {
    openPopover.value = null;
};

const onYearChange = () => {
    openPopover.value = null;
    tripsStore.getArchiveStats(selectedYear.value);
};

onMounted(() => {
    tripsStore.getArchiveStats();
});
</script>

<style scoped>
.archive-stats-select {
    color-scheme: light;
}
:where(.dark) .archive-stats-select {
    color-scheme: dark;
}
</style>
